/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */


/*
 * How an account labels itself in the shell (sidebar header, mobile header, mobile menu).
 *
 * Owners are businesses far more often than individuals, so the company name — when the profile
 * carries one — is the identity the user recognizes. The personal first/last name is the fallback
 * for accounts with no company on file. Same rule the Edit Owner Profile header already applies.
 */

/** The subset of the auth user these helpers read; keeps them usable for any name-carrying DTO. */
export interface NamedAccount {
  firstName?: string | null;
  lastName?: string | null;
  company?: string | null;
}

/** Company name when the profile has one, otherwise "First Last". Empty string if neither exists. */
export function accountDisplayName(user?: NamedAccount | null): string {
  if (!user) return '';
  const company = (user.company ?? '').trim();
  if (company) return company;
  return `${(user.firstName ?? '').trim()} ${(user.lastName ?? '').trim()}`.trim();
}

/**
 * Avatar initials for the same name: up to two letters, taken from the words of the company name
 * when a company is shown, otherwise from first + last name. Punctuation-only words such as the
 * "&" in "Tolls & Ticket Management" are skipped.
 */
export function accountInitials(user?: NamedAccount | null): string {
  if (!user) return '';
  const company = (user.company ?? '').trim();
  if (company) {
    return company
      .split(/\s+/)
      .map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''))
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0].toUpperCase())
      .join('');
  }
  const first = (user.firstName ?? '').trim();
  const last = (user.lastName ?? '').trim();
  return `${first ? first[0].toUpperCase() : ''}${last ? last[0].toUpperCase() : ''}`;
}
